import React, { useState } from 'react';
import {
  VStack,
  Box,
  Text,
  Heading,
  Button,
  HStack,
  useColorModeValue,
  Grid,
  GridItem,
  Badge,
  Divider,
  SimpleGrid,
  Image,
  Flex,
  AspectRatio,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Skeleton,
  Checkbox,
  Spinner
} from '@chakra-ui/react';
import {
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Images,
  Warning,
  Camera,
  Video,
  Star,
  ClipboardText,
  ListBullets,
  FileText,
  Globe,
  Flag,
  Notebook
} from '@phosphor-icons/react';
import { useCreateSite } from '../../../context/CreateSiteContext';
import CitationList from '../../site/CitationList';
import GroundingSources from '../../site/GroundingSources'; // Import GroundingSources
import SourceLinkedText from '../../site/SourceLinkedText'; // Import SourceLinkedText
import { renumberInTextCitations } from '../../../utils/citationUtils';
import SiteTimeline from '../../../components/site/SiteTimeline';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

// Custom hook to extract all theme values - Reuse from BasicInfoStep
const useThemeColors = () => {
  return {
    bgColor: useColorModeValue('white', 'gray.800'),
    accentColor: useColorModeValue('yellow.50', 'rgba(214, 173, 96, 0.1)'),
    borderHighlight: useColorModeValue('border.default', 'rgba(139, 109, 89, 0.5)'),
    borderColor: useColorModeValue('yellow.200', 'rgba(214, 173, 96, 0.3)'),
    textColor: useColorModeValue('inherit', 'inherit'), // Fallback to inherit
    mutedColor: useColorModeValue('text.muted', 'text.muted'),
    cardBg: useColorModeValue('bg.surface', 'bg.surface'),
    inputBg: useColorModeValue('bg.surface', 'bg.surface'),
    dividerColor: useColorModeValue('gray.200', 'gray.700')
  };
};

// Helper component for displaying section data
const SectionCard = ({ title, icon: Icon, children, hasIssues = false, isComplete = true }) => {
  const { cardBg, borderHighlight, mutedColor, textColor, dividerColor } = useThemeColors(); // Use theme hook
  
  // Determine border color based on issues, but use theme highlight otherwise
  const dynamicBorderColor = useColorModeValue(
    hasIssues ? 'red.200' : borderHighlight, // Use borderHighlight for default light
    hasIssues ? 'red.700' : borderHighlight  // Use borderHighlight for default dark
  );
  
  const titleColor = useColorModeValue(
    hasIssues ? 'red.500' : 'text.default',
    hasIssues ? 'red.300' : 'text.default'
  );
  const accentColor = useColorModeValue('brand.primary', 'brand.primary'); // Keep brand color for icon? Or use a theme color? Let's keep brand for now.

  return (
    <Box
      borderWidth="2px" // Increased border width
      borderRadius="md"
      borderColor={dynamicBorderColor} // Use dynamic border color
      bg={cardBg} // Use theme card background
      overflow="hidden"
      position="relative" // Needed for pseudo-element
      _before={{ // Parchment texture
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: useColorModeValue(0, 0.05), // Only show texture in dark mode
        pointerEvents: 'none',
        bgImage: 'var(--texture-parchment)',
        bgRepeat: 'repeat',
        bgSize: '300px',
        zIndex: 0
      }}
    >
      {/* Header */}
      <HStack 
        px={4} 
        py={3}
        bg={useColorModeValue('gray.50', 'rgba(0,0,0,0.1)')} // Slightly darker header in dark mode
        borderBottomWidth="1px"
        borderColor={dynamicBorderColor} // Use dynamic border color for consistency
        position="relative" // Ensure header is above texture
        zIndex={1}
      >
        <Icon size={18} color={hasIssues ? "red" : accentColor} />
        <Heading size="sm" color={titleColor} fontWeight="semibold">
          {title}
        </Heading>
        <Box flex="1" />
        {isComplete ? (
          <Badge colorScheme={hasIssues ? "red" : "green"} variant="subtle">
            <HStack spacing={1}>
              {hasIssues ? <Warning size={10} /> : <CheckCircle size={10} />}
              <Text fontSize="xs">{hasIssues ? "Issues" : "Complete"}</Text>
            </HStack>
          </Badge>
        ) : (
          <Badge colorScheme="yellow" variant="subtle">
            <HStack spacing={1}>
              <Warning size={10} />
              <Text fontSize="xs">Incomplete</Text>
            </HStack>
          </Badge>
        )}
      </HStack>
      
      {/* Body */}
      <Box 
        p={4} 
        position="relative" 
        zIndex={1} 
        sx={{ // Apply selection style within the card body
          '& ::selection': {
            background: 'rgba(139, 109, 89, 0.3)', // Brown selection background
          }
        }}
      > 
        {children}
      </Box>
    </Box>
  );
};

// Helper component for displaying field data
const FieldDisplay = ({ label, value, isRequired = false }) => {
  const textColor = useColorModeValue('inherit', 'inherit'); // Fallback to inherit
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  
  // Extract actual value if it's an object with value property
  const actualValue = typeof value === 'object' && value !== null && 'value' in value
    ? value.value
    : value;
    
  const isEmpty = !actualValue || (typeof actualValue === 'string' && !actualValue.trim());
  
  return (
    <Box mb={3}>
      <HStack alignItems="flex-start" spacing={1} mb={1}>
        <Text fontSize="sm" fontWeight="medium" color={mutedColor}>
          {label}
        </Text>
        {isRequired && isEmpty && (
          <Text color="red.500" fontSize="xs" fontWeight="bold">
            (Required)
          </Text>
        )}
      </HStack>
      
      {isEmpty ? (
        <Text fontSize="sm" color="red.500" fontStyle="italic">
          {isRequired ? "Required field - please complete" : "Not provided"}
        </Text>
      ) : (
        <Text fontSize="sm" color={textColor}>
          {actualValue}
        </Text>
      )}
    </Box>
  );
};

// Media preview component for review step
const MediaPreview = ({ media }) => {
  const isVideo = media.url?.includes('video') || media.contentType?.includes('video');
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  
  return (
    <Box 
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
      borderColor={useColorModeValue('gray.200', 'gray.600')}
    >
      <AspectRatio ratio={4/3} width="100%">
        <Box position="relative">
          {isVideo ? (
            <Flex
              align="center"
              justify="center"
              bg="black"
              h="100%"
              w="100%"
            >
              <Box
                as="video"
                src={media.url}
                objectFit="cover"
                w="100%"
                h="100%"
              />
              <Box
                position="absolute"
                bg="blackAlpha.700"
                p={2}
                borderRadius="full"
              >
                <Video size={16} color="white" />
              </Box>
            </Flex>
          ) : (
            <Image
              src={media.url}
              alt={media.caption || "Site media"}
              objectFit="cover"
              width="100%"
              height="100%"
              fallback={<Skeleton height="100%" width="100%" />}
            />
          )}
          
          {media.id === media.primaryMediaId && (
            <Badge
              position="absolute"
              top={2}
              left={2}
              colorScheme="yellow"
            >
              <HStack spacing={1}>
                <Star size={10} weight="fill" />
                <Text fontSize="2xs">Primary</Text>
              </HStack>
            </Badge>
          )}
        </Box>
      </AspectRatio>
      
      {media.caption && (
        <Box p={2}>
          <Text fontSize="xs" noOfLines={1} color={mutedColor}>
            {media.caption}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export const ReviewStep = () => {
  const {
    siteData,
    handleSubmit,
    validationErrors,
  } = useCreateSite();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Get theme colors for use in this component scope (e.g., for the submission box)
  const { cardBg, borderHighlight, mutedColor, textColor } = useThemeColors();
  
  // Handle submission
  const handleSubmitSite = async () => {
    if (!termsAccepted) {
      setSubmitError('You must agree to the terms before submitting');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const result = await handleSubmit();
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit site');
      }
      // The context's handleSubmit will handle navigation on success
    } catch (error) {
      console.error('Error submitting site:', error);
      setSubmitError(error.message || 'An unknown error occurred');
      setIsSubmitting(false);
    }
  };
  
  // Check for required fields
  const hasRequiredName = Boolean(siteData?.name?.trim());
  // const hasRequiredDescription = Boolean(siteData?.description?.trim()); // User wants this optional
  // const hasRequiredLocation = Boolean( // User wants this optional
  //   siteData?.location?.coordinates?.lat && siteData?.location?.coordinates?.lng
  // );
  
  // Check if submission is enabled - only name and terms are now strictly required for the button
  const canSubmit = hasRequiredName && termsAccepted;
  
  // Determine if description and location are complete for UI feedback, but not for submission blocking
  const isDescriptionComplete = Boolean(siteData?.description?.trim());
  const isLocationComplete = Boolean(siteData?.location?.coordinates?.lat && siteData?.location?.coordinates?.lng);

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading as="h1" size="lg" mb={2} fontFamily="heading">
            Review Your Site
          </Heading>
          <Text color={mutedColor}>
            Please review all information before submitting
          </Text>
        </Box>
        
        {/* Submission Error */}
        {submitError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle mr={2}>Submission Error</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Box>
          </Alert>
        )}
        
        {/* Review sections */}
        <Grid templateColumns="repeat(12, 1fr)" gap={6}>
          {/* Left Column */}
          <GridItem colSpan={{ base: 12, md: 6 }}>
            <VStack spacing={6} align="stretch">
              {/* Basic Info */}
              <SectionCard
                title="Basic Information"
                icon={ClipboardText}
                hasIssues={!hasRequiredName || !isDescriptionComplete} // Show issue if name or description is missing
                isComplete={hasRequiredName && isDescriptionComplete} // Complete if both name and description are present
              >
                <FieldDisplay label="Site Name" value={siteData.name} isRequired />
                <Accordion allowMultiple allowToggle defaultIndex={[0, 1]}> {/* Open first two sub-sections by default */}
                  {[
                    { field: 'overviewIntroduction', title: 'Overview / Introduction' },
                    { field: 'discoveryAndExcavation', title: 'Discovery and Excavation' },
                    { field: 'historicalContextAndChronology_narrative', title: 'Historical Context & Chronology' },
                    { field: 'keyArchitecturalFeaturesLayout', title: 'Key Architectural Features / Layout' },
                    { field: 'purposeAndFunction', title: 'Purpose and Function' },
                    { field: 'culturalSignificance_narrative', title: 'Cultural Significance' },
                    { field: 'notableFindsArtifacts_narrative', title: 'Notable Finds / Artifacts' },
                    { field: 'theoriesAndInterpretations', title: 'Theories and Interpretations' },
                    { field: 'currentStatusAndPreservation_narrative', title: 'Current Status and Preservation' },
                  ].map(({ field, title }) => {
                    const rawFieldData = siteData[field];
                    let textContent = '';
                    // Initialize fieldGroundingMetadata:
                    // Start with global siteData.groundingMetadata as a fallback,
                    // or an empty object if global is also missing.
                    let fieldGroundingMetadata = siteData.groundingMetadata || { groundingSupports: [] };

                    if (typeof rawFieldData === 'object' && rawFieldData !== null) {
                      textContent = rawFieldData.value || '';
                      // If field-specific metadata exists and is an object, prioritize it.
                      if (rawFieldData.groundingMetadata && typeof rawFieldData.groundingMetadata === 'object') {
                        fieldGroundingMetadata = rawFieldData.groundingMetadata;
                      }
                    } else if (typeof rawFieldData === 'string') {
                      textContent = rawFieldData;
                      // If rawFieldData is just a string, fieldGroundingMetadata remains as initialized
                      // (i.e., global or empty).
                    }

                    // Only render the AccordionItem if there's actual content after trimming
                    const trimmedTextContent = textContent.trim();
                    // const renumberedContent = trimmedTextContent ? renumberInTextCitations(trimmedTextContent, siteData.citations || []) : ""; // Removed renumbering

                    return trimmedTextContent ? ( // Check trimmedTextContent directly
                      <AccordionItem key={field} border="none">
                        <h2>
                          <AccordionButton px={0} py={2} _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}>
                            <Box flex="1" textAlign="left" fontWeight="medium" fontSize="sm" color={mutedColor}>
                              {title}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={3} pt={1} px={0}>
                          <SourceLinkedText
                            text={trimmedTextContent} // Use the original trimmed textContent
                            citations={siteData.citations || []}
                            groundingMetadata={fieldGroundingMetadata} 
                            onCitationClick={(citationId) => {
                              const clickedCitation = siteData.citations?.find(c => c.id === citationId);
                              if (clickedCitation) {
                                console.log('Clicked citation:', clickedCitation);
                              }
                            }}
                          />
                        </AccordionPanel>
                      </AccordionItem>
                    ) : null;
                  })}

                  {/* Display Site Name with SourceLinkedText if it might contain citations */}
                  {/* This was not explicitly in sectionsConfig but good practice if name can have citations */}
                  {siteData.name && (
                     <AccordionItem border="none">
                        <h2>
                          <AccordionButton px={0} py={2} _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}>
                            <Box flex="1" textAlign="left" fontWeight="medium" fontSize="sm" color={mutedColor}>
                              Site Name (Raw)
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={3} pt={1} px={0}>
                           <SourceLinkedText
                              text={siteData.name || ''}
                              citations={siteData.citations || []}
                              onCitationClick={(citationId) => {
                                const clickedCitation = siteData.citations?.find(c => c.id === citationId);
                                if (clickedCitation) {
                                  console.log('Clicked citation in name:', clickedCitation);
                                }
                              }}
                            />
                        </AccordionPanel>
                      </AccordionItem>
                  )}


                  {siteData.features && siteData.features.length > 0 && (
                    <AccordionItem border="none">
                      <h2>
                        <AccordionButton px={0} py={2} _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}>
                          <Box flex="1" textAlign="left" fontWeight="medium" fontSize="sm" color={mutedColor}>
                            Notable Features
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={3} pt={1} px={0}>
                        <VStack align="start" spacing={0}>
                          {siteData.features.map((feature, index) => (
                            <Text key={index} fontSize="sm" color={textColor}>
                              • {feature}
                            </Text>
                          ))}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  )}

                  {siteData.keyDiscoveries && siteData.keyDiscoveries.length > 0 && (
                    <AccordionItem border="none">
                      <h2>
                        <AccordionButton px={0} py={2} _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}>
                          <Box flex="1" textAlign="left" fontWeight="medium" fontSize="sm" color={mutedColor}>
                            Key Discoveries
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={3} pt={1} px={0}>
                        <VStack align="start" spacing={0}>
                          {siteData.keyDiscoveries.map((discovery, index) => (
                            <Text key={index} fontSize="sm" color={textColor}>
                              • {discovery}
                            </Text>
                          ))}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                </Accordion>
              </SectionCard>

              {/* Construction Periods */}
              <SectionCard
                title="Construction Timeline"
                icon={Clock}
                isComplete={true} // Assuming timeline data is optional or always considered complete for review
                hasIssues={false} // Add logic if timeline can have issues
              >
                <Box
                  mx={-4}
                  mb={-4}
                  borderTopWidth="1px"
                  borderTopColor={useColorModeValue('gray.100', 'gray.700')}
                >
                  <Box
                    className="timeline-wrapper"
                    borderRadius="0 0 md md"
                    overflow="hidden"
                  >
                    <SiteTimeline site={siteData} />
                  </Box>
                </Box>
              </SectionCard>

              {/* Media Gallery */}
              <SectionCard
                title="Media Gallery"
                icon={Images}
                isComplete={true} // Assuming media is optional or always complete for review
                hasIssues={false} // Add logic if media can have issues
              >
                {siteData.media && siteData.media.length > 0 ? (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={3}>
                      {siteData.media.length} media item(s)
                    </Text>
                    <SimpleGrid columns={{ base: 2, sm: 3, md: 3, lg: 4 }} spacing={3}> {/* Adjusted md/lg columns for half width */}
                      {siteData.media.slice(0, 8).map((media) => ( // Show up to 8 previews in half width
                        <MediaPreview
                          key={media.id}
                          media={{...media, primaryMediaId: siteData.primaryMediaId}}
                        />
                      ))}
                      {siteData.media.length > 8 && (
                        <Box
                          borderWidth="1px"
                          borderRadius="md"
                          borderColor={useColorModeValue('gray.200', 'gray.600')}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          p={4}
                        >
                          <VStack spacing={2}>
                            <Badge colorScheme="yellow" borderRadius="full" px={2}>
                              +{siteData.media.length - 8} more
                            </Badge>
                          </VStack>
                        </Box>
                      )}
                    </SimpleGrid>
                  </Box>
                ) : (
                  <Text fontSize="sm" color={mutedColor}>
                    No media uploaded. This is optional.
                  </Text>
                )}
              </SectionCard>
            </VStack>
          </GridItem>

          {/* Right Column */}
          <GridItem colSpan={{ base: 12, md: 6 }}>
            <VStack spacing={6} align="stretch">
              {/* Location */}
              <SectionCard
                title="Location"
                icon={MapPin}
                hasIssues={!isLocationComplete} // Show issue if location is missing
                isComplete={isLocationComplete} // Complete if location is present
              >
                {isLocationComplete ? (
                  <Box mb={3}>
                    <Text fontWeight="medium" fontSize="sm" mb={1}>Coordinates</Text>
                    <Badge colorScheme="green">
                      <HStack spacing={1}>
                        <MapPin size={12} />
                        <Text fontSize="xs">
                          {siteData.location.coordinates.lat.toFixed(6)}, {siteData.location.coordinates.lng.toFixed(6)}
                        </Text>
                      </HStack>
                    </Badge>
                  </Box>
                ) : (
                  <Box mb={3}>
                    <Text color="red.500" fontSize="sm">
                      Site location coordinates are recommended.
                    </Text>
                  </Box>
                )}
                <FieldDisplay label="Address" value={siteData.location.address} />
                <FieldDisplay label="Region" value={siteData.location.region} />
                <FieldDisplay label="Country" value={siteData.location.country} />
              </SectionCard>

              {/* Sources & Grounding */}
              <SectionCard
                title="Sources & Grounding"
                icon={Notebook}
                isComplete={true} // Sources are optional for submission
                hasIssues={false}
              >
                {(siteData.citations && siteData.citations.length > 0) || (siteData.groundingArticles && siteData.groundingArticles.length > 0) ? (
                  <Box>
                    {siteData.citations && siteData.citations.length > 0 && (
                      <Box mb={siteData.groundingArticles && siteData.groundingArticles.length > 0 ? 3 : 0}>
                        <CitationList citations={siteData.citations} />
                      </Box>
                    )}
                    {siteData.groundingArticles && siteData.groundingArticles.length > 0 && (
                      <Box
                        mt={siteData.citations && siteData.citations.length > 0 ? 3 : 0}
                        pt={siteData.citations && siteData.citations.length > 0 ? 3 : 0}
                        borderTopWidth={siteData.citations && siteData.citations.length > 0 ? "1px" : "0px"}
                        borderColor={useColorModeValue('gray.200', 'gray.700')}
                      >
                         <GroundingSources articles={siteData.groundingArticles} />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Text fontSize="sm" color={mutedColor}>
                    No sources or grounding information provided.
                  </Text>
                )}
              </SectionCard>
            </VStack>
          </GridItem>
        </Grid>
        
        {/* Terms and Submission */}
        <Box
          mt={4}
          p={4}
          borderWidth="2px" // Use 2px border
          borderRadius="md"
          borderColor={borderHighlight} // Use theme border highlight
          bg={cardBg} // Use theme card background
          position="relative" // Needed for pseudo-element
          overflow="hidden" // Needed for pseudo-element
          _before={{ // Parchment texture
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: useColorModeValue(0, 0.05), // Only show texture in dark mode
            pointerEvents: 'none',
            bgImage: 'var(--texture-parchment)',
            bgRepeat: 'repeat',
            bgSize: '300px',
            zIndex: 0
          }}
          sx={{ // Apply selection style within this box
            '& ::selection': {
              background: 'rgba(139, 109, 89, 0.3)', // Brown selection background
            }
          }}
        >
          <VStack align="stretch" spacing={4} position="relative" zIndex={1}> {/* Ensure content is above texture */}
            <Heading size="md">Ready to Submit?</Heading>
            
            <Checkbox
              size="md"
              isChecked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            >
              <Text fontSize="sm">
                I confirm that this information is accurate to the best of my knowledge and I have permission to share any uploaded media.
              </Text>
            </Checkbox>
            
            <HStack justify="space-between">
              <Text color={mutedColor} fontSize="sm">
                All submissions are reviewed by our team before publication
              </Text>
              <Button
                colorScheme="green"
                size="lg"
                leftIcon={<CheckCircle />}
                onClick={handleSubmitSite}
                isDisabled={!canSubmit}
                isLoading={isSubmitting}
                loadingText="Submitting..."
              >
                Submit Site
              </Button>
            </HStack>
          </VStack>
        </Box>
        
        {/* Validation warning - now only shows for Site Name if it's missing for submission blocking */}
        {(!hasRequiredName) && (
          <Alert status="warning" variant="left-accent">
            <AlertIcon />
            <Box>
              <AlertTitle>Missing Required Information for Submission</AlertTitle>
              <AlertDescription>
                <Text mb={2}>To submit the site, please complete the following:</Text>
                <VStack align="start" pl={4}>
                  {!hasRequiredName && (
                    <Text>• Site Name</Text>
                  )}
                </VStack>
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Informational alert for optional fields if they are missing */}
        {(!isDescriptionComplete || !isLocationComplete) && (
          <Alert status="info" variant="left-accent" mt={4}>
            <AlertIcon />
            <Box>
              <AlertTitle>Optional Information Missing</AlertTitle>
              <AlertDescription>
                <Text mb={2}>The following fields are recommended for a more complete site listing but are not required for submission:</Text>
                <VStack align="start" pl={4}>
                  {!isDescriptionComplete && (
                    <Text>• Site Description (Overview/Introduction)</Text>
                  )}
                  {!isLocationComplete && (
                    <Text>• Site Location (coordinates)</Text>
                  )}
                </VStack>
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};
